import React, { useState } from "react";
import { Box, TextField, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import TabsCst from "@components/TabsCst";
import { XapiActivityState } from "../types";
import { serializeStateContent } from "../config/xapiStates";

interface XapiStatesSectionProps {
  states: XapiActivityState[];
  drafts: Record<string, string>;
  invalidIds: string[];
  disabled?: boolean;
  onChange: (id: string, text: string) => void;
}

const XapiStatesSection: React.FC<XapiStatesSectionProps> = ({
  states,
  drafts,
  invalidIds,
  disabled,
  onChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const [activeId, setActiveId] = useState<string>(states[0]?.id ?? "");
  const active = states.find((state) => state.id === activeId) ?? states[0];

  if (states.length === 0) {
    return (
      <Typography sx={{ fontSize: "14px", color: theme.palette.text.primary }}>
        {t("xapi.states-empty")}
      </Typography>
    );
  }

  const text =
    active && drafts[active.id] !== undefined
      ? drafts[active.id]
      : serializeStateContent(active?.content);
  const isInvalid = !!active && invalidIds.includes(active.id);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TabsCst
        tabs={states.map((state) => ({ key: state.id, label: state.id }))}
        activeKey={active?.id ?? ""}
        onChange={setActiveId}
      />
      <TextField
        multiline
        minRows={5}
        fullWidth
        value={text}
        disabled={disabled}
        error={isInvalid}
        helperText={isInvalid ? String(t("xapi.state-invalid")) : undefined}
        onChange={(event) => active && onChange(active.id, event.target.value)}
        inputProps={{ style: { fontFamily: "inherit", fontSize: "14px" } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            color: theme.palette.text.primary,
          },
        }}
      />
    </Box>
  );
};

export default XapiStatesSection;
